// SERIES ZONE — public statistics about the market (BUILD-BRIEF-R6 §2.4, §2.5), on MarketPage
// after the Population zone.
//
// A series point is a READING OF A PUBLIC FILE, never a claim anyone on the record made: NO
// CLAIM MARK ANYWHERE IN THIS ZONE (§2.5). The `.ev-src` under an opened cell says who published
// the number and when — that citation carries the trust, not an `<Att>`.
//
// THE THREE HONESTIES THIS ZONE OWES THE OPERATOR, same family as MarketPage's four:
//
//   1. A PERIOD × GEOGRAPHY THE FILE NEVER PRINTED IS NOT ZERO. It renders `fv--unknown`, "not
//      collected" — the file was silent, not the value nil.
//   2. A PRINTED ZERO IS A ZERO. `fv--nil` — the source counted and found none; a different fact
//      from "nobody looked".
//   3. EVERY PERIOD THE STORE HOLDS, NEVER A WINDOW. The columns are not "the last five years";
//      they are every period a `<Fig>` could sit under, however many that is — the Sheet scrolls
//      horizontally INSIDE ITSELF so the page body never does.
//
// THE FOUR PRESETS (§2.5's table) are shortcuts into the picker, nothing more: each names the
// series it would show, and a preset none of whose series has landed yet (wave 1 fills only
// Childcare) says so in words rather than drawing an empty grid as though it had looked and
// found nothing.
//
// A SECOND SOURCE IS A SECOND ROW SET (§2.4): `also_from` never merges into the primary's rows —
// `source_key` is in the slot, and the zone draws two labelled tables rather than picking a
// winner.
//
// THE EVIDENCE. `routes/series.ts` (a sibling package) answers plain `series_points` rows — no
// document join, no per-point history array. A cell's citation is built here instead: the row's
// own `document_id` resolves against `GET /sources/:key/documents` (this zone's own read of the
// Sources package's already-published client, `api/sources.ts` — never re-implemented), cached
// per source so the same vintage list is fetched once; a superseded reading of the SAME slot
// (series · source · geo · period · dims) rides in the same `history: true` read and is matched
// by that slot key, never by the point's own id.
import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Fig, Lnk, Sep, Sheet, Tbl, Zone, ZoneHead } from './PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { listSeries, seriesCatalogue, type SeriesFilters, type SeriesGeography } from '../api/series.js';
import { listDocuments } from '../api/sources.js';
import {
  SERIES, dimLabel, seriesByKey, seriesLabel, type SeriesSpec,
} from '../../shared/series.v1.js';
import type { SeriesPoint } from '../../shared/series.v1.js';
import type { SourceDocument } from '../../shared/sources.v1.js';
import './SeriesZone.de.js';
import './SeriesZone.fr.js';

/** §2.5's table, by series key — a preset is a shortcut into the picker, not a fifth data path.
 *  A key not yet in `SERIES` (waves 2–3) is simply not offered until its module lands. */
const PRESETS: Array<{ key: string; label: string; keys: string[] }> = [
  { key: 'ri', label: 'RI beneficiaries', keys: ['vd.ri.rate_18_64_pct', 'vd.ri.dossiers', 'vd.ri.beneficiaries'] },
  { key: 'non_take_up', label: 'Non-take-up', keys: ['vd.non_take_up_pct', 'vd.poverty_rate_pct'] },
  { key: 'childcare', label: 'Childcare', keys: [
    'vd.aje.places', 'vd.aje.coverage_pct', 'vd.aje.institutions', 'vd.faje.funding_share_pct', 'vd.aje.total_cost_chf',
  ] },
  { key: 'child_protection', label: 'Child protection', keys: [
    'vd.dgej.situations', 'vd.dgej.placements', 'vd.dgej.institutions', 'vd.dgej.foyers', 'vd.dgej.places', 'vd.dgej.budget_chf',
  ] },
];

/** The dims object's canonical key — sorted, so a row (or a slot match) groups the same
 *  combination regardless of the order the server happened to write the keys in. */
function dimsKeyOf(dims: Record<string, string> | null | undefined): string {
  const d = dims ?? {};
  return Object.keys(d).sort().map((k) => `${k}=${d[k]}`).join('|');
}

/** The unique index's own columns (§2.1) — two points share a slot, and therefore a "was … in
 *  the file of …" relationship, when every one of these matches. */
function slotKeyOf(p: SeriesPoint): string {
  return [p.series_key, p.source_key, p.geo_level, p.geo_code, p.period, dimsKeyOf(p.dims)].join('\u0000');
}

// ── documents, cached per source (a citation's filename/last_modified/snapshot_id) ───────────

const sourceDocsCache = new Map<string, SourceDocument[] | null>();

function useDocumentsBySource(sourceKey: string | null): Map<number, SourceDocument> {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!sourceKey || sourceDocsCache.has(sourceKey)) return;
    let cancelled = false;
    listDocuments(sourceKey)
      .then((r) => { if (!cancelled) { sourceDocsCache.set(sourceKey, r.items); setTick((n) => n + 1); } })
      .catch(() => { if (!cancelled) { sourceDocsCache.set(sourceKey, null); setTick((n) => n + 1); } });
    return () => { cancelled = true; };
  }, [sourceKey]);
  const out = new Map<number, SourceDocument>();
  const list = sourceKey ? sourceDocsCache.get(sourceKey) : null;
  if (list) for (const d of list) out.set(d.id, d);
  return out;
}

interface RowDef {
  key: string;
  label: ReactNode;
}

/** Rows are geography, UNLESS the series is canton-only and carries dims — then a row is one
 *  combination of dimension tokens (§2.5: "or, for a canton-only series, the dimension"). */
function rowsOf(spec: SeriesSpec, items: SeriesPoint[], geographies: SeriesGeography[], lang: 'en' | 'de' | 'fr', t: (k: string) => string): RowDef[] {
  if (spec.geo_level === 'canton' && spec.dims.length) {
    const seen = new Map<string, Record<string, string>>();
    for (const it of items) {
      const dk = dimsKeyOf(it.dims);
      if (!seen.has(dk)) seen.set(dk, it.dims ?? {});
    }
    return [...seen.entries()]
      .map(([key, dims]) => ({
        key,
        label: spec.dims.map((d) => dimLabel(lang, d, dims[d] ?? '')).filter(Boolean).join(' · ') || t('canton'),
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }
  return geographies
    .map((g) => ({ key: `${g.geo_level}:${g.geo_code}`, label: g.geo_name }))
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

function CellValue({ point, t, fmtNum }: {
  point: SeriesPoint | undefined;
  t: (k: string) => string;
  fmtNum: (n: number | string | null | undefined) => string;
}) {
  if (!point) return <span className="fv--unknown">{t('not collected')}</span>;
  // A printed zero is a counted fact, not silence — the two must never look the same (P11).
  return <Fig className={point.value === 0 ? 'fv--nil' : undefined}>{fmtNum(point.value)}</Fig>;
}

function EvidencePanel({ point, history, docs, t, fmtDate }: {
  point: SeriesPoint;
  /** Every OTHER point sharing this one's slot — the vintages it either superseded or was
   *  superseded by, oldest first. */
  history: SeriesPoint[];
  docs: Map<number, SourceDocument>;
  t: (k: string, v?: Record<string, string | number>) => string;
  fmtDate: (iso: string | null | undefined) => string;
}) {
  const doc = docs.get(point.document_id);
  return (
    <div className="fgroup">
      <blockquote className="ev">{point.quote}</blockquote>
      <div className="ev-src">
        {doc?.filename ?? point.source_key}
        {' '}<Sep />{' '}
        {doc?.last_modified ? <Fig>{fmtDate(doc.last_modified)}</Fig>
          : point.as_of ? <Fig>{fmtDate(point.as_of)}</Fig>
          : <span className="fv--unknown">{t('not collected')}</span>}
        {doc?.snapshot_id != null && (
          <> {' '}<Sep />{' '}<Lnk href={`/api/snapshots/${doc.snapshot_id}/file`}>{t('Open')}</Lnk></>
        )}
      </div>
      {!!history.length && (
        <div className="zone-note">
          {history.map((h) => (
            <div key={h.id}>
              {t('was {value} in the file of {date}', {
                value: h.value, date: h.as_of ? fmtDate(h.as_of) : t('not collected'),
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** One source's row set — a `Sheet clip` whose inner scroller carries the period columns, never
 *  the page. Labelled by source when it is the secondary of an `also_from` pair. */
function SeriesTable({ spec, sourceKey, sourceLabel, lang, t, fmtDate, fmtNum }: {
  spec: SeriesSpec;
  /** `undefined` reads the series' own primary source. */
  sourceKey?: string;
  sourceLabel?: string | null;
  lang: 'en' | 'de' | 'fr';
  t: (k: string, v?: Record<string, string | number>) => string;
  fmtDate: (iso: string | null | undefined) => string;
  fmtNum: (n: number | string | null | undefined) => string;
}) {
  const [data, setData] = useState<{ items: SeriesPoint[]; total: number; periods: string[]; geographies: SeriesGeography[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);

  const resolvedSource = sourceKey ?? spec.source_key;
  const docs = useDocumentsBySource(resolvedSource);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    const filters: SeriesFilters = { series: spec.key, source: sourceKey, history: true };
    listSeries(filters)
      .then((r) => { if (live) setData(r); })
      .catch((err) => { if (live) setError(err instanceof Error ? err.message : String(err)); });
    return () => { live = false; };
  }, [spec.key, sourceKey]);

  // Current points only feed the grid's rows and cells; the history read's superseded points
  // are kept aside, grouped by slot, purely for the evidence panel (§2.4).
  const current = useMemo(() => (data ? data.items.filter((p) => p.status === 'current') : []), [data]);
  const historyBySlot = useMemo(() => {
    const out = new Map<string, SeriesPoint[]>();
    if (!data) return out;
    for (const p of data.items) {
      if (p.status !== 'superseded') continue;
      const key = slotKeyOf(p);
      const list = out.get(key) ?? [];
      list.push(p);
      out.set(key, list);
    }
    for (const list of out.values()) list.sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
    return out;
  }, [data]);

  const rows = useMemo(
    () => (data ? rowsOf(spec, current, data.geographies, lang, t) : []),
    [data, current, spec, lang, t],
  );
  const periods = data?.periods ?? [];

  const pointAt = useCallback((rowKey: string, period: string): SeriesPoint | undefined => {
    if (spec.geo_level === 'canton' && spec.dims.length) {
      return current.find((it) => it.period === period && dimsKeyOf(it.dims) === rowKey);
    }
    return current.find((it) => it.period === period && `${it.geo_level}:${it.geo_code}` === rowKey);
  }, [current, spec]);

  if (error) {
    return <p className="zone-empty">{t('The series store did not answer: {error}', { error })}</p>;
  }
  if (!data) return <p className="zone-empty">{t('Loading…')}</p>;
  if (!rows.length || !periods.length) {
    return (
      <p className="zone-empty">
        {t('No point has landed for this series yet — the parser that fills it has not run, or has found nothing to file.')}
      </p>
    );
  }

  return (
    <div className="fgroup">
      {sourceLabel != null && <div className="u-label">{sourceLabel}</div>}
      <Sheet clip>
        <div style={{ overflowX: 'auto' }}>
          <Tbl>
            <thead>
              <tr>
                <th>{spec.geo_level === 'canton' && spec.dims.length ? t('Dimension') : t('Geography')}</th>
                {periods.map((p) => <th className="num" key={p}>{p}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const openHere = periods.find((period) => openKey === `${row.key}|${period}`);
                const openPoint = openHere ? pointAt(row.key, openHere) : undefined;
                return (
                  <Fragment key={row.key}>
                    <tr>
                      <td>{row.label}</td>
                      {periods.map((period) => {
                        const point = pointAt(row.key, period);
                        const cellKey = `${row.key}|${period}`;
                        return (
                          <td className="num" key={period}>
                            {point ? (
                              <button
                                type="button"
                                className="lnk"
                                style={{ font: 'inherit', letterSpacing: 'inherit', color: 'inherit' }}
                                onClick={() => setOpenKey((k) => (k === cellKey ? null : cellKey))}
                                title={t('Show the file this figure was read from')}
                              >
                                <CellValue point={point} t={t} fmtNum={fmtNum} />
                              </button>
                            ) : (
                              <CellValue point={undefined} t={t} fmtNum={fmtNum} />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {openPoint && (
                      <tr>
                        <td colSpan={periods.length + 1}>
                          <EvidencePanel
                            point={openPoint}
                            history={historyBySlot.get(slotKeyOf(openPoint)) ?? []}
                            docs={docs}
                            t={t}
                            fmtDate={fmtDate}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Tbl>
        </div>
      </Sheet>
    </div>
  );
}

export default function SeriesZone() {
  const t = useT();
  const { lang, fmtDate, fmtNum } = useLocale();

  const [catalogueEmpty, setCatalogueEmpty] = useState<boolean | null>(null);

  useEffect(() => {
    let live = true;
    seriesCatalogue()
      .then((r) => { if (live) setCatalogueEmpty(r.items.every((row) => row.sources.every((s) => !s.count))); })
      .catch(() => { if (live) setCatalogueEmpty(null); });
    return () => { live = false; };
  }, []);

  const sourcesInOrder = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const s of SERIES) if (!seen.has(s.source_key)) { seen.add(s.source_key); out.push(s.source_key); }
    return out;
  }, []);

  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const [seriesKey, setSeriesKey] = useState<string>(SERIES[0]?.key ?? '');
  const [extraSource, setExtraSource] = useState<string | null>(null);
  const [presetKey, setPresetKey] = useState<string | null>(null);

  const spec = seriesByKey(seriesKey);

  const applyPreset = useCallback((p: typeof PRESETS[number]) => {
    setPresetKey(p.key);
    const found = p.keys.map((k) => seriesByKey(k)).find((s): s is SeriesSpec => !!s);
    if (found) { setSeriesKey(found.key); setSourceFilter(null); setExtraSource(null); }
  }, []);

  const visibleSeries = useMemo(
    () => (sourceFilter ? SERIES.filter((s) => s.source_key === sourceFilter) : SERIES),
    [sourceFilter],
  );

  const seriesBySource = useMemo(() => {
    const out = new Map<string, SeriesSpec[]>();
    for (const s of visibleSeries) {
      const list = out.get(s.source_key) ?? [];
      list.push(s);
      out.set(s.source_key, list);
    }
    return out;
  }, [visibleSeries]);

  const preset = presetKey ? PRESETS.find((p) => p.key === presetKey) ?? null : null;
  const presetHasData = preset ? preset.keys.some((k) => seriesByKey(k)) : true;

  return (
    <Zone id="series">
      <ZoneHead
        label={t('Series')}
        note={t('Public statistics about the market, not about one organisation — the file, the row and the date behind every figure')}
      />

      <div className="instr-band">
        <div className="filter-chips">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`chip${presetKey === p.key ? ' active' : ''}`}
              onClick={() => applyPreset(p)}
            >
              {t(p.label)}
            </button>
          ))}
        </div>
      </div>

      <div className="instr-band">
        <div className="filter-chips">
          <span className="u-label self-center">{t('Source')}</span>
          <button
            type="button"
            className={`chip${sourceFilter === null ? ' active' : ''}`}
            onClick={() => setSourceFilter(null)}
          >
            {t('All')}
          </button>
          {sourcesInOrder.map((s) => (
            <button
              key={s}
              type="button"
              className={`chip${sourceFilter === s ? ' active' : ''}`}
              onClick={() => setSourceFilter((cur) => (cur === s ? null : s))}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="search-wrapper ml-auto">
          <select
            className="search-input"
            value={seriesKey}
            onChange={(e) => { setSeriesKey(e.target.value); setPresetKey(null); setExtraSource(null); }}
            aria-label={t('Series')}
          >
            {[...seriesBySource.entries()].map(([src, list]) => (
              <optgroup key={src} label={src}>
                {list.map((s) => (
                  <option key={s.key} value={s.key}>{seriesLabel(lang, s.key)}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </div>

      {spec?.also_from?.length ? (
        <div className="instr-band">
          <div className="filter-chips">
            <span className="u-label self-center">{t('Also from')}</span>
            <button type="button" className={`chip${!extraSource ? ' active' : ''}`} onClick={() => setExtraSource(null)}>
              {spec.source_key}
            </button>
            {spec.also_from.map((s) => (
              <button
                key={s}
                type="button"
                className={`chip${extraSource === s ? ' active' : ''}`}
                onClick={() => setExtraSource((cur) => (cur === s ? null : s))}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {spec ? (
        <>
          <p className="zone-note">{spec.definition}</p>
          <SeriesTable spec={spec} lang={lang} t={t} fmtDate={fmtDate} fmtNum={fmtNum} />
          {extraSource && (
            <SeriesTable spec={spec} sourceKey={extraSource} sourceLabel={extraSource} lang={lang} t={t} fmtDate={fmtDate} fmtNum={fmtNum} />
          )}
        </>
      ) : preset && !presetHasData ? (
        <p className="zone-empty">
          {t('No series behind this preset has been built yet — a later wave of the sources programme fills it.')}
        </p>
      ) : (
        <p className="zone-empty">{t('Pick a series to read its figures.')}</p>
      )}

      {catalogueEmpty && (
        <p className="zone-note">{t('nothing has answered yet')}</p>
      )}
    </Zone>
  );
}
